import React, { useEffect, lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { SocketProvider, useSocketContext } from "./contexts/SocketContext";
import { useAuth } from "./contexts/AuthContext";
// ThemeProvider already wrapped in main.jsx - no need to wrap again
// Import role and persona providers
import { UserRoleProvider, useUserRole } from "./contexts/UserRoleContext";
import { UserPersonaProvider } from "./contexts/UserPersonaContext";
import { DialerProvider } from "./contexts/DialerContext"; // Import DialerProvider
import { SipProvider } from "./contexts/SipContext"; // Import SipProvider for WebRTC/SIP
import { IntegratedDialerProvider } from "./contexts/IntegratedDialerContext"; // Import IntegratedDialerProvider
import { LoadingProvider } from "./contexts/LoadingContext"; // Import LoadingProvider
import { toast } from "react-hot-toast";
import Layout from "./components/layout/Layout";
import ErrorBoundary from "./components/ErrorBoundary"; // Import ErrorBoundary
// Using standard ErrorBoundary component
import RouteErrorBoundary from "./components/ui/RouteErrorBoundary"; // Import RouteErrorBoundary
import ActiveCallWarning from "./components/ActiveCallWarning"; // Import Active Call Warning component
import CookieConsent from "./components/CookieConsent"; // Import Cookie Consent banner
import performanceService from "./services/performanceService"; // Import performance monitoring
import { homeForUser, requiresSetup } from "./utils/setupAccess";
import SetupAccessBoundary from "./components/auth/SetupAccessBoundary";
import { WORKSPACE_REDIRECTS } from "./pages/workspace/redirects";
import PreservingRedirect from "./components/routing/PreservingRedirect";
import { LEGACY_ADMIN_REDIRECTS } from "./components/organization/sections";
import RoleDenied from "./components/routing/RoleDenied";
import { LEGACY_ROUTE_REDIRECTS } from "./components/routing/legacyRedirects";
import AskNexusRedirect from "./components/assistant/AskNexusRedirect";
import { RouteFallback, ShellSkeleton } from "./components/shell/ShellSkeleton";

// Suspense fallback: inside the workspace a content skeleton under the
// standing shell; elsewhere a quiet canvas (components/shell/ShellSkeleton).
const PageLoader = RouteFallback;

// LazyPage component to wrap lazy-loaded components with Suspense
const LazyPage = ({ children }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
);

// LazyProtectedPage component to wrap both ProtectedRoute and Suspense
const LazyProtectedPage = ({ children, allowedRoles, deniedTo, deniedNotice }) => (
  <ProtectedRoute allowedRoles={allowedRoles} deniedTo={deniedTo} deniedNotice={deniedNotice}>
    <Layout>
      <SocketNotifications />
      <Suspense fallback={<PageLoader />}>{children}</Suspense>
    </Layout>
  </ProtectedRoute>
);

// Lazy-loaded components with code splitting
// High-priority routes with prefetch (common entry points)
const Login = lazy(
  () => import(/* webpackPrefetch: true */ "./pages/auth/Login"),
);
const Dashboard = lazy(
  () => import(/* webpackPrefetch: true */ "./pages/Dashboard"),
);
const ContactsPage = lazy(
  () => import(/* webpackPrefetch: true */ "./pages/Contacts"),
);

// Standard lazy-loaded components
const UserOnboarding = lazy(
  () => import("./components/onboarding/UserOnboarding"),
);
const HomePage = lazy(() => import("./pages/HomePage.jsx"));
const DealsPage = lazy(() => import("./pages/Deals"));
const ContactDetailPage = lazy(() => import("./pages/ContactDetail"));
const CampaignsPage = lazy(() => import("./pages/Campaigns"));
const ReportsPage = lazy(() => import("./pages/Reports"));
const SettingsPage = lazy(() => import("./pages/Settings"));
const CampaignView = lazy(() => import("./pages/CampaignView"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unauthorized = lazy(() =>
  import("./pages/NotFound").then((module) => ({ default: module.Unauthorized })),
);
const DialerPage = lazy(() => import("./pages/Dialer"));
const SessionRoutes = lazy(() => import("./pages/session/SessionRoutes"));
const Register = lazy(() => import("./pages/auth/Register"));
const DeleteAccount = lazy(() => import("./pages/auth/DeleteAccount"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Companies = lazy(() => import("./pages/Companies"));
const CompanyDetails = lazy(() => import("./pages/CompanyDetails"));
const LandingPage = lazy(() => import("./pages/LandingPage"));

// Lazy-loaded admin pages
const OrganizationPage = lazy(() => import("./pages/Organization"));
const CoachHome = lazy(() => import("./pages/coach/CoachHome"));
const CallReview = lazy(() => import("./pages/coach/CallReview"));
const TeamCoaching = lazy(() => import("./pages/coach/TeamCoaching"));
const FloorPage = lazy(() => import("./pages/Floor"));
const CallsPage = lazy(() => import("./pages/calls/CallsPage"));
const ReportsAI = lazy(() => import("./pages/reports/ReportsAI"));
const AcceptInvitation = lazy(() => import("./pages/auth/AcceptInvitation"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const OAuthCallback = lazy(() => import("./pages/auth/OAuthCallback"));

const AI_INSIGHT_TOAST_ID = "ai-insight";

/**
 * SocketNotifications routes AI insights into the Signal toaster (mounted
 * once in Layout). A fixed id keeps it to a single toast, so remounting on
 * navigation never stacks duplicates.
 */
const SocketNotifications = () => {
  const { lastAiInsight } = useSocketContext();

  // AI insights show for five seconds.
  useEffect(() => {
    if (!lastAiInsight?.insight) return;
    const message =
      lastAiInsight.insight.message || JSON.stringify(lastAiInsight.insight);
    toast(`AI Insight: ${message}`, {
      id: AI_INSIGHT_TOAST_ID,
      duration: 5000,
    });
  }, [lastAiInsight]);

  // The development-only "Real-time connection error" notice now lives in
  // the call dock as a quiet second line (components/shell/CallDock.jsx).

  return null;
};

// Development environment check is used directly in the JSX

// Route Guard component with role checking
// A role that cannot use a route goes to the nearest page it can use
// (`deniedTo`), with a one-line notice when the move needs explaining.
const ProtectedRoute = ({ children, allowedRoles, deniedTo = "/dashboard", deniedNotice }) => {
  const { isSignedIn, isLoaded, user } = useAuth();
  const { role: userRole } = useUserRole();

  // Paint the workspace frame at once while sign-in resolves; it fills in
  // when the user (and so their sidebar) is known.
  if (!isLoaded) return <ShellSkeleton />;

  if (!isSignedIn) {
    return <Navigate to="/login" replace />;
  }

  if (requiresSetup(user)) return <Navigate to="/setup" replace />;

  if (allowedRoles && userRole && !allowedRoles.includes(userRole)) {
    return <RoleDenied to={deniedTo} notice={deniedNotice} />;
  }

  return children;
};

// Root route: show landing page to guests, redirect authenticated users to dashboard
const LandingPageRoute = () => {
  const { isSignedIn, isLoaded, user } = useAuth();

  if (!isLoaded) return <RouteFallback />;

  if (isSignedIn) {
    return <Navigate to={homeForUser(user)} replace />;
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <LandingPage />
    </Suspense>
  );
};

// 404 and 403: a guest gets the public page; a signed-in person gets the
// same page inside the shell, so a wrong link never drops them out of it.
const ErrorRoute = ({ code }) => {
  const { isSignedIn, isLoaded, user } = useAuth();
  if (!isLoaded) return <RouteFallback />;
  const Page = code === 403 ? Unauthorized : NotFound;
  if (isSignedIn && !requiresSetup(user)) {
    return (
      <Layout>
        <Suspense fallback={<PageLoader />}>
          <Page inShell />
        </Suspense>
      </Layout>
    );
  }
  return (
    <Suspense fallback={<PageLoader />}>
      <Page />
    </Suspense>
  );
};

// Removed PrivateDialerRoutes - using standard role-based protection instead

/**
 * Main Application Component
 */
const App = () => {
  const { user, isLoaded } = useAuth();
  // Initialize enhanced performance monitoring
  useEffect(() => {
    performanceService.init({
      enableResourceTiming: true,
      enableUserTiming: true,
      enableLongTaskMonitoring: true,
      enableMemoryMonitoring: true,
      enableBundleSizeTracking: true,
      sampleRate: import.meta.env.MODE === "production" ? 0.1 : 1.0, // 10% sampling in production
      reportingThreshold: {
        lcp: 2500,
        fid: 100,
        cls: 0.1,
        ttfb: 600,
        longTask: 50,
        bundleSize: 500 * 1024,
      },
    });

    const initialReportTimeout =
      import.meta.env.MODE === "development"
        ? window.setTimeout(() => {
            console.log(
              "📊 Initial App Performance:",
              performanceService.getEnhancedPerformanceReport(),
            );
          }, 5000)
        : null;

    return () => {
      if (initialReportTimeout !== null) {
        window.clearTimeout(initialReportTimeout);
      }
      performanceService.cleanup();
    };
  }, []);

  // Define roles for clarity
  const ROLES = {
    SUPER_ADMIN: "super_admin",
    ADMIN: "admin",
    MANAGER: "manager",
    CLOSER: "closer", // Added Closer
    FRONTER: "fronter", // Added Fronter
    // Keep 'AGENT' for now if it's used elsewhere, or refactor later
    AGENT: "agent",
  };

  return (
    <SetupAccessBoundary>
      <CookieConsent />
      <ErrorBoundary>
        <LoadingProvider>
          <ErrorBoundary>
            <UserRoleProvider>
              <ErrorBoundary>
                <UserPersonaProvider>
                  <ErrorBoundary>
                    <SocketProvider>
                      <ErrorBoundary>
                        <SipProvider>
                          <ErrorBoundary>
                            <DialerProvider>
                              <ErrorBoundary>
                                <IntegratedDialerProvider>
                                  <ActiveCallWarning />
                                  <Routes errorElement={<RouteErrorBoundary />}>
                                    {/* ── Organization, personal settings, Ask Nexus and merged views
                                         (docs/design/INFORMATION_ARCHITECTURE.md). Old paths redirect
                                         with their params and query string intact. ── */}
                                    <Route
                                      path="/organization/*"
                                      element={
                                        <LazyProtectedPage allowedRoles={[ROLES.ADMIN]}>
                                          <ErrorBoundary type="page" name="Organization">
                                            <OrganizationPage />
                                          </ErrorBoundary>
                                        </LazyProtectedPage>
                                      }
                                    />
                                    {[...WORKSPACE_REDIRECTS, ...LEGACY_ADMIN_REDIRECTS, ...LEGACY_ROUTE_REDIRECTS].map(([from, to]) => (
                                      <Route key={from} path={from} element={<PreservingRedirect to={to} />} />
                                    ))}
                                    <Route
                                      path="/settings/:section"
                                      element={
                                        <LazyProtectedPage>
                                          <ErrorBoundary type="page" name="Settings">
                                            <SettingsPage />
                                          </ErrorBoundary>
                                        </LazyProtectedPage>
                                      }
                                    />
                                    <Route path="/virtual-manager" element={<AskNexusRedirect />} />

                                    <Route
                                      path="/setup"
                                      element={isLoaded ? <Navigate to={user ? homeForUser(user) : "/login"} replace /> : <PageLoader />}
                                    />
                                    {/* Auth routes - no layout wrapper, accessible to all */}
                                    <Route
                                      path="/login"
                                      element={
                                        <LazyPage>
                                          <Login />
                                        </LazyPage>
                                      }
                                    />
                                    <Route
                                      path="/register"
                                      element={
                                        <LazyPage>
                                          <Register />
                                        </LazyPage>
                                      }
                                    />
                                    <Route
                                      path="/invite/:token"
                                      element={
                                        <LazyPage>
                                          <AcceptInvitation />
                                        </LazyPage>
                                      }
                                    />
                                    <Route
                                      path="/reset-password"
                                      element={
                                        <LazyPage>
                                          <ResetPassword />
                                        </LazyPage>
                                      }
                                    />
                                    <Route
                                      path="/auth/oauth/callback"
                                      element={
                                        <LazyPage>
                                          <OAuthCallback />
                                        </LazyPage>
                                      }
                                    />
                                    <Route
                                      path="/delete-account"
                                      element={
                                        <LazyPage>
                                          <DeleteAccount />
                                        </LazyPage>
                                      }
                                    />
                                    <Route
                                      path="/privacy"
                                      element={
                                        <LazyPage>
                                          <PrivacyPolicy />
                                        </LazyPage>
                                      }
                                    />
                                    <Route
                                      path="/legal/privacy"
                                      element={
                                        <LazyPage>
                                          <PrivacyPolicy />
                                        </LazyPage>
                                      }
                                    />
                                    <Route
                                      path="/onboarding"
                                      element={
                                        <ProtectedRoute>
                                          <Layout>
                                            <SocketNotifications />
                                            <Suspense fallback={<PageLoader />}>
                                              <ErrorBoundary
                                                type="page"
                                                name="Onboarding"
                                              >
                                                <UserOnboarding
                                                  onComplete={() =>
                                                    (window.location.href =
                                                      "/dashboard")
                                                  }
                                                />
                                              </ErrorBoundary>
                                            </Suspense>
                                          </Layout>
                                        </ProtectedRoute>
                                      }
                                    />

                                    {/* --- Protected Routes --- */}
                                    {/* Wrap element prop with ProtectedRoute and pass allowedRoles */}

                                    {/* Fix for broken links - ensure all routes are properly defined */}
                                    {/* Add root route to redirect to dashboard */}

                                    {/* Root route - landing page for guests, dashboard for authenticated */}
                                    <Route
                                      path="/"
                                      element={<LandingPageRoute />}
                                    />

                                    {/* Generic Dashboard - Maybe redirect based on role later? */}
                                    <Route
                                      path="/dashboard"
                                      element={
                                        <LazyProtectedPage
                                          allowedRoles={[
                                            ROLES.ADMIN,
                                            ROLES.MANAGER,
                                            ROLES.CLOSER,
                                            ROLES.FRONTER,
                                            ROLES.AGENT,
                                          ]}
                                        >
                                          <ErrorBoundary
                                            type="page"
                                            name="Dashboard"
                                          >
                                            <Dashboard />
                                          </ErrorBoundary>
                                        </LazyProtectedPage>
                                      }
                                    />

                                    {/* Coach, Floor, Team and Calls, plus redirects from the old dashboards */}
                                    <Route
                                      path="/coach"
                                      element={
                                        <LazyProtectedPage
                                          allowedRoles={[
                                            ROLES.ADMIN,
                                            ROLES.MANAGER,
                                            ROLES.CLOSER,
                                            ROLES.FRONTER,
                                            ROLES.AGENT,
                                          ]}
                                        >
                                          <ErrorBoundary type="page" name="CoachHome">
                                            <CoachHome />
                                          </ErrorBoundary>
                                        </LazyProtectedPage>
                                      }
                                    />
                                    <Route
                                      path="/floor"
                                      element={
                                        <LazyProtectedPage
                                          allowedRoles={[ROLES.ADMIN, ROLES.MANAGER]}
                                        >
                                          <ErrorBoundary type="page" name="Floor">
                                            <FloorPage />
                                          </ErrorBoundary>
                                        </LazyProtectedPage>
                                      }
                                    />
                                    <Route
                                      path="/team"
                                      element={
                                        <LazyProtectedPage
                                          allowedRoles={[ROLES.ADMIN, ROLES.MANAGER]}
                                        >
                                          <ErrorBoundary type="page" name="TeamCoaching">
                                            <TeamCoaching />
                                          </ErrorBoundary>
                                        </LazyProtectedPage>
                                      }
                                    />
                                    <Route
                                      path="/team/:userId"
                                      element={
                                        <LazyProtectedPage
                                          allowedRoles={[ROLES.ADMIN, ROLES.MANAGER]}
                                        >
                                          <ErrorBoundary type="page" name="CoachHome">
                                            <CoachHome />
                                          </ErrorBoundary>
                                        </LazyProtectedPage>
                                      }
                                    />
                                    <Route
                                      path="/calls"
                                      element={
                                        <LazyProtectedPage
                                          allowedRoles={[
                                            ROLES.ADMIN,
                                            ROLES.MANAGER,
                                            ROLES.CLOSER,
                                            ROLES.FRONTER,
                                            ROLES.AGENT,
                                          ]}
                                        >
                                          <ErrorBoundary type="page" name="Calls">
                                            <CallsPage />
                                          </ErrorBoundary>
                                        </LazyProtectedPage>
                                      }
                                    />
                                    <Route
                                      path="/calls/:callId"
                                      element={
                                        <LazyProtectedPage
                                          allowedRoles={[
                                            ROLES.ADMIN,
                                            ROLES.MANAGER,
                                            ROLES.CLOSER,
                                            ROLES.FRONTER,
                                            ROLES.AGENT,
                                          ]}
                                        >
                                          <ErrorBoundary type="page" name="CallReview">
                                            <CallReview />
                                          </ErrorBoundary>
                                        </LazyProtectedPage>
                                      }
                                    />
                                    <Route
                                      path="/reports/ai"
                                      element={
                                        <LazyProtectedPage
                                          allowedRoles={[ROLES.ADMIN]}
                                          deniedTo="/reports"
                                          deniedNotice="AI analysis is available to admins."
                                        >
                                          <ErrorBoundary type="page" name="ReportsAI">
                                            <ReportsAI />
                                          </ErrorBoundary>
                                        </LazyProtectedPage>
                                      }
                                    />
                                    <Route
                                      path="/dialer"
                                      element={
                                        <ProtectedRoute
                                          allowedRoles={[
                                            ROLES.FRONTER,
                                            ROLES.CLOSER,
                                            ROLES.MANAGER,
                                            ROLES.ADMIN,
                                            ROLES.AGENT,
                                          ]}
                                        >
                                          {" "}
                                          {/* All authenticated users */}
                                          <Layout>
                                            <SocketNotifications />
                                            <Suspense fallback={<PageLoader />}>
                                              <ErrorBoundary
                                                type="page"
                                                name="Dialer"
                                              >
                                                <DialerPage />
                                              </ErrorBoundary>
                                            </Suspense>
                                          </Layout>
                                        </ProtectedRoute>
                                      }
                                    />

                                    {/* Session mode: full screen, outside the workspace layout. */}
                                    <Route
                                      path="/session/*"
                                      element={
                                        <ProtectedRoute
                                          allowedRoles={[
                                            ROLES.FRONTER,
                                            ROLES.CLOSER,
                                            ROLES.MANAGER,
                                            ROLES.ADMIN,
                                            ROLES.AGENT,
                                          ]}
                                        >
                                          <Suspense fallback={<PageLoader />}>
                                            <SessionRoutes />
                                          </Suspense>
                                        </ProtectedRoute>
                                      }
                                    />

                                    {/* Removed duplicate root route - using dashboard redirect above */}

                                    {/* Deals routes with nested paths */}
                                    <Route
                                      path="/deals"
                                      element={
                                        <ProtectedRoute
                                          allowedRoles={[
                                            ROLES.ADMIN,
                                            ROLES.MANAGER,
                                            ROLES.CLOSER,
                                            ROLES.FRONTER,
                                            ROLES.AGENT,
                                          ]}
                                        >
                                          {" "}
                                          {/* Every role reads deals; writes are manager+ (the page hides them) */}
                                          <Layout>
                                            <SocketNotifications />
                                            <Suspense fallback={<PageLoader />}>
                                              <ErrorBoundary
                                                type="page"
                                                name="Deals"
                                              >
                                                <DealsPage />
                                              </ErrorBoundary>
                                            </Suspense>
                                          </Layout>
                                        </ProtectedRoute>
                                      }
                                      errorElement={<RouteErrorBoundary />}
                                    />
                                    <Route
                                      path="/deals/:id"
                                      element={
                                        <ProtectedRoute
                                          allowedRoles={[
                                            ROLES.ADMIN,
                                            ROLES.MANAGER,
                                            ROLES.CLOSER,
                                            ROLES.FRONTER,
                                            ROLES.AGENT,
                                          ]}
                                        >
                                          <Layout>
                                            <SocketNotifications />
                                            <Suspense fallback={<PageLoader />}>
                                              <ErrorBoundary
                                                type="page"
                                                name="DealsDetail"
                                              >
                                                <DealsPage />
                                              </ErrorBoundary>
                                            </Suspense>
                                          </Layout>
                                        </ProtectedRoute>
                                      }
                                    />

                                    {/* Contacts routes with nested paths */}
                                    <Route
                                      path="/contacts"
                                      element={
                                        <ProtectedRoute
                                          allowedRoles={[
                                            ROLES.ADMIN,
                                            ROLES.MANAGER,
                                            ROLES.CLOSER,
                                            ROLES.FRONTER,
                                            ROLES.AGENT,
                                          ]}
                                        >
                                          {" "}
                                          {/* Managers and up (the API is :manager_up) */}
                                          <Layout>
                                            <SocketNotifications />
                                            <Suspense fallback={<PageLoader />}>
                                              <ErrorBoundary
                                                type="page"
                                                name="Contacts"
                                              >
                                                <ContactsPage />
                                              </ErrorBoundary>
                                            </Suspense>
                                          </Layout>
                                        </ProtectedRoute>
                                      }
                                      errorElement={<RouteErrorBoundary />}
                                    />
                                    <Route
                                      path="/contacts/:id"
                                      element={
                                        <ProtectedRoute
                                          allowedRoles={[
                                            ROLES.ADMIN,
                                            ROLES.MANAGER,
                                            ROLES.CLOSER,
                                            ROLES.FRONTER,
                                            ROLES.AGENT,
                                          ]}
                                        >
                                          <Layout>
                                            <SocketNotifications />
                                            <Suspense fallback={<PageLoader />}>
                                              <ErrorBoundary
                                                type="page"
                                                name="ContactDetail"
                                              >
                                                <ContactDetailPage />
                                              </ErrorBoundary>
                                            </Suspense>
                                          </Layout>
                                        </ProtectedRoute>
                                      }
                                    />

                                    {/* Customers routes with nested paths */}

                                    {/* Campaigns routes with nested paths */}
                                    <Route
                                      path="/campaigns"
                                      element={
                                        <ProtectedRoute
                                          allowedRoles={[ROLES.ADMIN, ROLES.MANAGER]}
                                          deniedTo="/dialer"
                                        >
                                          {" "}
                                          {/* Allow all roles */}
                                          <Layout>
                                            <SocketNotifications />
                                            <Suspense fallback={<PageLoader />}>
                                              <ErrorBoundary
                                                type="page"
                                                name="Campaigns"
                                              >
                                                <CampaignsPage />
                                              </ErrorBoundary>
                                            </Suspense>
                                          </Layout>
                                        </ProtectedRoute>
                                      }
                                      errorElement={<RouteErrorBoundary />}
                                    />

                                    {/* Important: Order matters - put more specific routes first */}
                                    <Route
                                      path="/campaigns/new"
                                      element={
                                        <ProtectedRoute
                                          allowedRoles={[
                                            ROLES.ADMIN,
                                            ROLES.MANAGER,
                                          ]}
                                        >
                                          <Layout>
                                            <SocketNotifications />
                                            <Suspense fallback={<PageLoader />}>
                                              <ErrorBoundary
                                                type="page"
                                                name="CampaignNew"
                                              >
                                                <CampaignsPage isNew={true} />
                                              </ErrorBoundary>
                                            </Suspense>
                                          </Layout>
                                        </ProtectedRoute>
                                      }
                                    />

                                    <Route
                                      path="/campaigns/:id"
                                      element={
                                        <ProtectedRoute
                                          allowedRoles={[ROLES.ADMIN, ROLES.MANAGER]}
                                          deniedTo="/dialer"
                                        >
                                          <Layout>
                                            <SocketNotifications />
                                            <Suspense fallback={<PageLoader />}>
                                              <ErrorBoundary
                                                type="page"
                                                name="CampaignView"
                                              >
                                                <CampaignView />
                                              </ErrorBoundary>
                                            </Suspense>
                                          </Layout>
                                        </ProtectedRoute>
                                      }
                                    />

                                    {/* Contacts have no lead status, so /leads is an alias for /contacts. */}
                                    <Route
                                      path="/leads"
                                      element={<Navigate to="/contacts" replace />}
                                    />

                                    {/* Companies routes */}
                                    <Route
                                      path="/companies"
                                      element={
                                        <ProtectedRoute
                                          allowedRoles={[
                                            ROLES.ADMIN,
                                            ROLES.MANAGER,
                                            ROLES.CLOSER,
                                            ROLES.FRONTER,
                                            ROLES.AGENT,
                                          ]}
                                        >
                                          <Layout>
                                            <SocketNotifications />
                                            <Suspense fallback={<PageLoader />}>
                                              <ErrorBoundary
                                                type="page"
                                                name="Companies"
                                              >
                                                <Companies />
                                              </ErrorBoundary>
                                            </Suspense>
                                          </Layout>
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/companies/:id"
                                      element={
                                        <ProtectedRoute
                                          allowedRoles={[
                                            ROLES.ADMIN,
                                            ROLES.MANAGER,
                                            ROLES.CLOSER,
                                            ROLES.FRONTER,
                                            ROLES.AGENT,
                                          ]}
                                        >
                                          <Layout>
                                            <SocketNotifications />
                                            <Suspense fallback={<PageLoader />}>
                                              <ErrorBoundary
                                                type="page"
                                                name="CompanyDetails"
                                              >
                                                <CompanyDetails />
                                              </ErrorBoundary>
                                            </Suspense>
                                          </Layout>
                                        </ProtectedRoute>
                                      }
                                    />

                                    {/* Reports routes with subroutes */}
                                    <Route
                                      path="/reports"
                                      element={
                                        <ProtectedRoute
                                          allowedRoles={[
                                            ROLES.ADMIN,
                                            ROLES.MANAGER,
                                          ]}
                                        >
                                          {" "}
                                          {/* Admins and Managers */}
                                          <Layout>
                                            <SocketNotifications />
                                            <Suspense fallback={<PageLoader />}>
                                              <ErrorBoundary
                                                type="page"
                                                name="Reports"
                                              >
                                                <ReportsPage />
                                              </ErrorBoundary>
                                            </Suspense>
                                          </Layout>
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/reports/calls"
                                      element={
                                        <ProtectedRoute
                                          allowedRoles={[
                                            ROLES.ADMIN,
                                            ROLES.MANAGER,
                                          ]}
                                        >
                                          <Layout>
                                            <SocketNotifications />
                                            <Suspense fallback={<PageLoader />}>
                                              <ErrorBoundary
                                                type="page"
                                                name="ReportsCalls"
                                              >
                                                <ReportsPage section="calls" />
                                              </ErrorBoundary>
                                            </Suspense>
                                          </Layout>
                                        </ProtectedRoute>
                                      }
                                    />
                                    <Route
                                      path="/reports/agents"
                                      element={
                                        <ProtectedRoute
                                          allowedRoles={[
                                            ROLES.ADMIN,
                                            ROLES.MANAGER,
                                          ]}
                                        >
                                          <Layout>
                                            <SocketNotifications />
                                            <Suspense fallback={<PageLoader />}>
                                              <ErrorBoundary
                                                type="page"
                                                name="ReportsAgents"
                                              >
                                                <ReportsPage section="agents" />
                                              </ErrorBoundary>
                                            </Suspense>
                                          </Layout>
                                        </ProtectedRoute>
                                      }
                                    />

                                    {/* Settings routes — accessible to all authenticated users */}
                                    <Route
                                      path="/settings"
                                      element={
                                        <ProtectedRoute>
                                          <Layout>
                                            <SocketNotifications />
                                            <Suspense fallback={<PageLoader />}>
                                              <ErrorBoundary
                                                type="page"
                                                name="Settings"
                                              >
                                                <SettingsPage />
                                              </ErrorBoundary>
                                            </Suspense>
                                          </Layout>
                                        </ProtectedRoute>
                                      }
                                    />
                                    {/* 404 Page for unmatched routes */}
                                    <Route path="*" element={<ErrorRoute code={404} />} />
                                    <Route
                                      path="/unauthorized"
                                      element={<ErrorRoute code={403} />}
                                    />
                                  </Routes>
                                </IntegratedDialerProvider>
                              </ErrorBoundary>
                            </DialerProvider>
                          </ErrorBoundary>
                        </SipProvider>
                      </ErrorBoundary>
                    </SocketProvider>
                  </ErrorBoundary>
                </UserPersonaProvider>
              </ErrorBoundary>
            </UserRoleProvider>
          </ErrorBoundary>
        </LoadingProvider>
      </ErrorBoundary>
    </SetupAccessBoundary>
  );
};

export default App;
